import { useState } from "react";
import { Copy, Mic, X, Volume2, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";

interface TranslationPanelProps {
  value: string;
  onChange?: (value: string) => void;
  placeholder: string;
  isInput?: boolean;
  isLoading?: boolean;
  maxLength?: number;
  showVoiceInput?: boolean;
}

export function TranslationPanel({
  value,
  onChange,
  placeholder,
  isInput = false,
  isLoading = false,
  maxLength = 5000,
  showVoiceInput = false,
}: TranslationPanelProps) {
  const { toast } = useToast();
  const [isCopied, setIsCopied] = useState(false);

  const handleCopy = async () => {
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
      setIsCopied(true);
      toast({
        title: "Copied!",
        description: "Text copied to clipboard",
        duration: 2000,
      });

      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      toast({
        title: "Failed to copy",
        description: "Could not copy text to clipboard",
        variant: "destructive",
      });
    }
  };

  const handleClear = () => {
    if (onChange) {
      onChange("");
    }
  };

  const handleVoiceInput = () => {
    toast({
      title: "Voice input",
      description: "Voice input feature coming soon!",
    });
  };

  return (
    <div className="flex flex-col h-full bg-translator-panel rounded-lg border border-translator-border shadow-soft hover:shadow-medium transition-smooth group">
      {/* Header */}
      <div className="flex items-center justify-between p-3 sm:p-4 border-b border-translator-border">
        <h2 className="font-medium text-foreground text-base">
          {isInput ? "Source Text" : "Translation"}
        </h2>
        <div className="flex items-center gap-2">
          {value && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCopy}
              className="text-muted-foreground hover:text-foreground transition-smooth"
              title="Copy text"
              aria-label="Copy text"
            >
              <Copy className="h-4 w-4" />
            </Button>
          )}
          {!isInput && value && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => toast({ title: "Text-to-speech", description: "Feature coming soon!" })}
              className="text-muted-foreground hover:text-foreground transition-smooth"
              title="Listen to pronunciation"
              aria-label="Listen to pronunciation"
            >
              <Volume2 className="h-4 w-4" />
            </Button>
          )}
          {isInput && value && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClear}
              className="text-muted-foreground hover:text-destructive transition-smooth"
              title="Clear text"
              aria-label="Clear text"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 p-3 sm:p-4 relative">
        {isInput ? (
          <div className="relative h-full">
            <Textarea
              value={value}
              onChange={(e) => onChange?.(e.target.value)}
              placeholder={placeholder}
              className="w-full h-full min-h-[160px] sm:min-h-[200px] resize-none border-0 bg-transparent text-base leading-relaxed focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-muted-foreground transition-all duration-200"
              maxLength={maxLength}
            />

            {/* Voice Input Button */}
            {showVoiceInput && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleVoiceInput}
                className="absolute bottom-2 right-2 text-muted-foreground hover:text-foreground hover:bg-muted transition-smooth touch-target"
                title="Voice input"
                aria-label="Voice input"
              >
                <Mic className="h-5 w-5 sm:h-4 sm:w-4" />
              </Button>
            )}
          </div>
        ) : (
          <div className="h-full min-h-[160px] sm:min-h-[200px] relative">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center h-full gap-4 animate-fade-in">
                <div className="relative">
                  <div className="w-12 h-12 border-4 border-muted rounded-full"></div>
                  <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin absolute top-0"></div>
                </div>
                <div className="text-center">
                  <div className="text-muted-foreground font-medium">Translating your text...</div>
                  <div className="text-sm text-muted-foreground/70 mt-1">Powered by AI</div>
                </div>
              </div>
            ) : value ? (
              <div className="text-base leading-relaxed text-foreground whitespace-pre-wrap break-words animate-fade-in">
                {value}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-center animate-fade-in">
                <div className="mb-3 opacity-50">
                  <Zap className="h-12 w-12 text-muted-foreground" />
                </div>
                <div className="text-muted-foreground text-base font-medium mb-1">
                  {placeholder}
                </div>
                <div className="text-sm text-muted-foreground/70">
                  Your AI-powered translation will appear here
                </div>
              </div>
            )}
          </div>
        )}

        {/* Character Counter for Input */}
        {isInput && (
          <div className="absolute bottom-2 left-4 text-xs text-muted-foreground">
            {value.length} / {maxLength}
          </div>
        )}
      </div>
    </div>
  );
}